import { z } from 'zod';
import {
    DETAILED_REPORT_PROFILE_CATALOG,
    getDetailedReportProfileDefinition,
} from './report-catalog.js';
import {
    REPORT_PROFILE_SCHEMA_VERSION,
    REPORT_PROFILE_TOP_N_LIMIT,
} from './report-profile.schema.js';

export {
    CreateReportProfileSchema,
    ReportProfilePublicSchema,
    ReportProfilePublicWriteSchema,
    ReportProfileSchema,
    UpdateReportProfileSchema,
} from './report-profile.schema.js';

export const DETAILED_REPORT_PROFILE_SCHEMA_VERSION = 1;
export const DETAILED_REPORT_KEYS = Object.freeze(Object.values(DETAILED_REPORT_PROFILE_CATALOG)
    .filter((definition) => definition.capabilities.schedule && definition.capabilities.snapshot)
    .map((definition) => definition.key));
export const DETAILED_REPORT_PROFILE_KEYS = Object.freeze(Object.keys(DETAILED_REPORT_PROFILE_CATALOG));
export const DETAILED_REPORT_SECTION_KEYS = [
    'metrics',
    'charts',
    'records',
    'dataQuality',
    'methodology',
];

const optionalText = (max) => z.string().trim().max(max).optional().nullable();

export const DetailedReportProfileSectionSchema = z.object({
    key: z.enum(DETAILED_REPORT_SECTION_KEYS),
    enabled: z.boolean().default(true),
    order: z.number().int().min(0).max(DETAILED_REPORT_SECTION_KEYS.length - 1),
    topN: z.number().int().min(1).max(REPORT_PROFILE_TOP_N_LIMIT).optional(),
    visibleFields: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
    options: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
}).strict();

export const DetailedReportProfileBrandingSchema = z.object({
    accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#1f4e78'),
    coverTitle: optionalText(200),
    subtitle: optionalText(300),
    headerText: optionalText(300),
    footerText: optionalText(300),
    preparedBy: optionalText(200),
    showDate: z.boolean().default(true),
    showPageNumber: z.boolean().default(true),
}).strict();

const profileFilterScalar = z.union([
    z.string().max(500),
    z.number().finite(),
    z.boolean(),
]);

const savedFilterSchema = z.object({
    search: z.string().max(200).optional(),
    filters: z.record(z.string().max(150), z.array(profileFilterScalar).max(100)).optional(),
    serverFilters: z.record(
        z.string().max(160),
        z.union([profileFilterScalar, z.array(profileFilterScalar).max(100)]),
    ).optional(),
}).strict();

export const DetailedReportProfileScopeSchema = z.object({
    mode: z.enum(['filtered', 'selected', 'single', 'tenant']).default('filtered'),
    savedFilter: savedFilterSchema.optional(),
    sortBy: z.string().trim().max(80).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
}).strict();

const sharingSchema = z.object({
    visibility: z.enum(['private', 'tenant', 'users']).default('private'),
    userIds: z.array(z.string().trim().min(1).max(80)).max(100).default([]),
}).strict();

const profileWritableShape = {
    name: z.string().trim().min(1).max(120),
    reportKey: z.enum(DETAILED_REPORT_PROFILE_KEYS),
    locale: z.enum(['tr', 'en']).default('tr'),
    status: z.enum(['draft', 'active', 'inactive']).default('draft'),
    sharing: sharingSchema.default({ visibility: 'private', userIds: [] }),
    confidentialityClassId: z.string().trim().min(1).max(80).optional().nullable(),
    branding: DetailedReportProfileBrandingSchema.default({ accentColor: '#1f4e78' }),
    sections: z.array(DetailedReportProfileSectionSchema).min(1).max(DETAILED_REPORT_SECTION_KEYS.length),
    scope: DetailedReportProfileScopeSchema,
};

function uniqueSections(value, ctx) {
    const definition = getDetailedReportProfileDefinition(value.reportKey);
    if (definition && value.scope && !definition.scopeModes.includes(value.scope.mode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'mode'], message: 'Scope mode does not match report key' });
    }
    if (definition?.kind === 'dossier'
        && value.sections
        && !value.sections.some((section) => section.key === 'records' && section.enabled)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sections'], message: 'Dossier profiles must include records' });
    }
    const keys = value.sections?.map((section) => section.key) || [];
    const orders = value.sections?.map((section) => section.order) || [];
    if (new Set(keys).size !== keys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sections'], message: 'Section keys must be unique' });
    }
    if (new Set(orders).size !== orders.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sections'], message: 'Section order values must be unique' });
    }
}

export const CreateDetailedReportProfileSchema = z.object(profileWritableShape)
    .strict()
    .superRefine(uniqueSections);

export const UpdateDetailedReportProfileSchema = z.object({
    expectedVersion: z.number().int().positive(),
    ...Object.fromEntries(Object.entries(profileWritableShape).map(([key, schema]) => [key, schema.optional()])),
}).strict()
    .superRefine(uniqueSections)
    .refine((value) => Object.keys(value).some((key) => key !== 'expectedVersion'), {
        message: 'At least one profile field must be updated',
    });

export const UpdateDetailedReportProfileSharingSchema = z.object({
    expectedVersion: z.number().int().positive(),
    sharing: sharingSchema,
}).strict();


export const DetailedReportProfileGenerationSchema = z.object({
    profileId: z.string().trim().min(1).max(80),
    expectedProfileVersion: z.number().int().positive().optional(),
    scope: z.object({
        mode: z.enum(['filtered', 'selected', 'single', 'tenant']),
        recordIds: z.array(z.string().trim().min(1).max(80)).min(1).max(5000).optional(),
    }).strict().optional(),
}).strict();

function plainProfile(raw) {
    if (typeof raw?.toObject === 'function') return raw.toObject({ flattenMaps: true });
    return JSON.parse(JSON.stringify(raw || {}));
}

export function translateDetailedReportProfileV1ToV2(raw, {
    definitionVersion,
    profileContract = { key: 'report-profile-v2', version: 1 },
    migrationAudit,
} = {}) {
    const value = plainProfile(raw);
    if (value.schemaVersion === REPORT_PROFILE_SCHEMA_VERSION) return value;
    if (value.schemaVersion !== DETAILED_REPORT_PROFILE_SCHEMA_VERSION) {
        throw new Error('Detailed report profile schema version is not supported');
    }
    const sections = [...(value.sections || [])].sort((left, right) => left.order - right.order);
    const resolved = value.confidentiality?.resolvedSnapshot || value.confidentiality || {};
    const classId = String(value.confidentiality?.classId || 'legacy-default');
    return {
        id: String(value._id || value.id || ''),
        tenant: String(value.tenant || ''),
        owner: String(value.owner || ''),
        createdBy: String(value.createdBy || value.owner || ''),
        updatedBy: String(value.updatedBy || value.owner || ''),
        createdAt: value.createdAt || new Date(0),
        updatedAt: value.updatedAt || value.createdAt || new Date(0),
        schemaVersion: REPORT_PROFILE_SCHEMA_VERSION,
        reportKey: value.reportKey,
        definitionVersionAtSave: definitionVersion || value.definitionVersionAtSave || 1,
        profileContract,
        name: value.name,
        locale: value.locale || 'tr',
        status: value.status || 'draft',
        scope: {
            mode: value.scope?.mode || 'filtered',
            values: {
                ...(value.scope?.savedFilter ? { savedFilter: value.scope.savedFilter } : {}),
                ...(value.scope?.sortBy ? { sortBy: value.scope.sortBy } : {}),
                ...(value.scope?.sortOrder ? { sortOrder: value.scope.sortOrder } : {}),
            },
        },
        selectorDefaults: value.selectorDefaults || {},
        confidentiality: {
            classId,
            resolvedSnapshot: {
                code: resolved.code || 'INTERNAL',
                label: resolved.label || 'Internal',
                level: Number.isInteger(resolved.level) ? resolved.level : 0,
                color: resolved.color || '#1f4e78',
                ...(resolved.watermark ? { watermark: resolved.watermark } : {}),
            },
        },
        options: {
            sectionVisibility: Object.fromEntries(sections.map((section) => [section.key, section.enabled !== false])),
            sectionOrder: sections.map((section) => section.key),
            visibleFields: [...new Set(sections.flatMap((section) => section.visibleFields || []))],
            topN: sections.reduce((maximum, section) => Math.max(maximum, section.topN || 0), 0) || undefined,
        },
        sharing: {
            visibility: value.sharing?.visibility === 'users' ? 'shared' : value.sharing?.visibility || 'private',
            userIds: (value.sharing?.userIds || []).map(String),
        },
        isDefault: Boolean(value.isDefault),
        customFields: value.customFields || {},
        profileVersion: value.profileVersion || 1,
        ...(migrationAudit ? { migrationAudit } : {}),
    };
}
